<template>
  <div class="duel-invite-overlay">
    <div class="duel-invite">
      <div class="duel-plate" :style="plateStyle" aria-hidden="true"></div>

      <header class="duel-head">
        <p class="duel-eyebrow">
          <span class="duel-tick" aria-hidden="true"></span>
          {{ $t('fightHud.invitationEyebrow') }}
          <span class="duel-tick" aria-hidden="true"></span>
        </p>
        <h2 class="duel-title">{{ $t('fightHud.invitationTitle') }}</h2>
        <p class="duel-body">{{ $t('fightHud.invitationBody', {name: inviterLabel}) }}</p>
      </header>

      <div class="duel-stage">
        <!-- The portrait comes off the existing fight stack rather than a fresh
             <img>: FightCharacter forwards into RPGAvatar, which already owns
             both the empty-avatar and the broken-file fallbacks. FightSide is
             the wrong size here — it carries a half-screen figure plate. -->
        <FightCharacter class="duel-inviter" :character="inviterCard" :role="$t('fightHud.invitationRole')"/>

        <div v-if="deadline && !expired" class="duel-clock">
          <CountDown :deadline="deadline" @expired="handleExpired"/>
          <p class="duel-clock-label">{{ $t('fightHud.timeLeft') }}</p>
        </div>
      </div>

      <p v-if="pathLabel" class="duel-path">
        <span class="duel-path-key">{{ $t('fightHud.invitationPathLabel') }}</span>
        {{ pathLabel }}
      </p>

      <p v-if="expired" class="duel-expired">{{ $t('fightHud.invitationExpired') }}</p>
      <p v-if="error" class="duel-error">{{ error }}</p>

      <div class="duel-actions">
        <template v-if="expired">
          <HudButton :type="ButtonType.CANCEL" @click="handleDismiss">
            {{ $t('fightHud.dismiss') }}
          </HudButton>
        </template>
        <template v-else>
          <HudButton :type="ButtonType.SUBMIT" :disabled="submitting" @click="handleAccept">
            {{ $t('fightHud.accept') }}
          </HudButton>
          <HudButton :type="ButtonType.CANCEL" :disabled="submitting" @click="handleReject">
            {{ $t('fightHud.reject') }}
          </HudButton>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CountDown from '@/components/Game/Fight/CountDown.vue';
import FightCharacter from '@/components/Fight/FightCharacter.vue';
import HudButton from '@/components/btn/HudButton.vue';
import {ButtonType} from '@/types/ButtonType';

/*
 * The duel invitation. A scrim and one modal: who is calling you out, how long
 * you have to answer, and the two answers.
 *
 * No opening phrase here. `resolveFightPhrase` seeds on two fighters and
 * interpolates {p1}/{p2}; an invitation has an inviter and *you*, so half the
 * bank would read as a line about a stranger.
 */
export default {
  name: 'FightChallengeOverlay',
  components: {CountDown, FightCharacter, HudButton},
  setup() {
    return {ButtonType};
  },
  props: {
    challenge: {
      type: Object,
      required: true,
    },
    backgroundUrl: {
      type: String,
      default: '',
    },
  },
  emits: ['accept', 'reject', 'dismiss'],
  data() {
    return {
      submitting: false,
      error: null,
      expired: false,
    };
  },
  computed: {
    /* The full `Fighter` the invitation carries. Tolerates the older shape,
       where `inviter` was only the id. */
    inviter() {
      const inviter = this.challenge?.inviter;
      return inviter && typeof inviter === 'object' ? inviter : null;
    },
    inviterLabel() {
      return this.inviter?.name || this.challenge?.inviterName || '';
    },
    inviterCard() {
      return {
        name: this.inviterLabel,
        title: this.inviter?.rank_name || '',
        avatar: this.inviter?.avatar || undefined,
        game_master_mode: Boolean(this.inviter?.game_master_mode),
      };
    },
    pathLabel() {
      return this.inviter?.path_name || '';
    },
    deadline() {
      return this.challenge?.expiresAt || null;
    },
    plateStyle() {
      return this.backgroundUrl
          ? {backgroundImage: `url(${this.backgroundUrl})`}
          : {};
    },
  },
  watch: {
    challenge() {
      this.expired = false;
    },
  },
  methods: {
    handleAccept() {
      if (this.submitting || this.expired) {
        return;
      }
      this.submitting = true;
      this.error = null;
      this.$emit('accept', this.challenge);
    },
    handleReject() {
      if (this.submitting || this.expired) {
        return;
      }
      this.submitting = true;
      this.error = null;
      this.$emit('reject', this.challenge);
    },
    /* The invitation has a real server-side TTL. Once it runs out the server
       would refuse both answers, so the overlay stops offering them and turns
       into an acknowledgement the player dismisses. */
    handleExpired() {
      this.expired = true;
      this.submitting = false;
      this.error = null;
    },
    handleDismiss() {
      this.$emit('dismiss', this.challenge);
    },
    // Exposed for the caller: this component performs no I/O of its own (never call
    // generated API classes from a component), so whoever owns the accept/reject
    // service call reports the outcome back through these two.
    fail(message) {
      this.submitting = false;
      this.error = message || this.$t('fightHud.error');
    },
    reset() {
      this.submitting = false;
      this.error = null;
    },
  },
};
</script>

<style scoped>
/* The scrim is the established recipe, shared with the establishment donut and
   the fight start overlay. */
.duel-invite-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  background: rgba(2, 8, 14, 0.55);
}

.duel-invite {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: clamp(1.25rem, 3vw, 2.25rem) clamp(1.25rem, 4vw, 2.75rem);
  width: min(92vw, 34rem);
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid var(--pl-border);
  border-radius: var(--pl-radius-lg, 0.75rem);
  box-shadow: 0 1.5rem 4rem rgba(2, 8, 14, 0.75), inset 0 0 3rem rgba(2, 8, 14, 0.6);
  text-align: center;
}

/* The scene behind the invitation. Empty until the duel bank lands in
   `assets/images/backgrounds/duel`, at which point `useLocationBackground('duel', …)`
   fills it; the painted gradient is what shows through until then, and if the
   bank is ever removed. */
.duel-plate {
  position: absolute;
  inset: 0;
  background:
      radial-gradient(circle at 50% 22%, rgba(95, 216, 242, 0.18), transparent 58%),
      linear-gradient(165deg, #060d13 0%, #0b1720 55%, #05090d 100%);
  background-size: cover;
  background-position: center;
  pointer-events: none;
}

/* A wash over the plate so a bright photograph never eats the type. */
.duel-plate::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
      radial-gradient(circle at 50% 35%, rgba(6, 13, 19, 0.35), rgba(6, 13, 19, 0.85) 78%),
      linear-gradient(to bottom, rgba(6, 13, 19, 0.45), rgba(6, 13, 19, 0.9));
}

.duel-invite > *:not(.duel-plate) {
  position: relative;
  z-index: 1;
}

/* ---- Header ------------------------------------------------------------- */

.duel-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.duel-eyebrow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  margin: 0;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-label);
  font-weight: 600;
  letter-spacing: var(--pl-track-eyebrow);
  text-transform: uppercase;
  color: rgba(126, 220, 232, 0.72);
}

.duel-tick {
  width: 2.2rem;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(126, 220, 232, 0.55), transparent);
}

.duel-title {
  margin: 0;
  font-family: var(--pl-font-display);
  font-weight: 700;
  font-size: clamp(1.05rem, 2.4vw, 1.6rem);
  line-height: var(--pl-leading-tight);
  letter-spacing: var(--pl-track-title);
  text-transform: uppercase;
  color: var(--pl-text);
  text-shadow: 0 0 1.5rem rgba(95, 216, 242, 0.3), 0 2px 12px rgba(2, 8, 14, 0.95);
}

.duel-body {
  margin: 0;
  max-width: 26rem;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-base);
  line-height: var(--pl-leading-prose);
  text-wrap: balance;
  color: var(--pl-text-muted);
}

/* ---- The inviter and the clock ------------------------------------------ */

.duel-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(0.5rem, 2vw, 1.25rem);
  width: 100%;
}

.duel-inviter {
  flex: 1 1 auto;
  min-width: 0;
}

.duel-clock {
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.duel-clock-label {
  margin: 0;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-micro);
  letter-spacing: var(--pl-track-label);
  text-transform: uppercase;
  color: var(--pl-text-dim);
}

.duel-path {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-xs);
  letter-spacing: var(--pl-track-label);
  text-transform: uppercase;
  color: var(--pl-text-muted);
}

.duel-path-key {
  font-size: var(--pl-text-micro);
  letter-spacing: var(--pl-track-eyebrow);
  color: var(--pl-text-dim);
}

.duel-expired {
  margin: 0;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-sm);
  color: var(--pl-text-muted);
}

.duel-error {
  margin: 0;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-sm);
  color: var(--pl-danger, #e0645c);
}

.duel-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

@media (max-width: 560px) {
  .duel-stage {
    flex-direction: column;
  }
}
</style>
